import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Printer, Clock, Calendar, Edit3, Trash2, GraduationCap, Users, Search } from 'lucide-react';
import { Card, Button, Input } from '../../components/ui/index.jsx';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { TimetableModal } from './TimetableModal';

const DAYS = ['الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

export const TimetableList = () => {
    const navigate = useNavigate();
    const { data: response, refresh, loading } = useGet('/timetables');
    const { hasPermission } = useAuth();
    const { success, error: toastError } = useToast();

    const canCreate = hasPermission('school-classes', 'create');
    const canDelete = hasPermission('school-classes', 'delete');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedClass, setSelectedClass] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const classes = Array.isArray(response) ? response : [];

    const handleOpenModal = (cls = null) => {
        setSelectedClass(cls);
        setIsModalOpen(true);
    };

    const handleDeleteTimetable = async (classId, className) => {
        if (confirm(`هل أنت متأكد من حذف استعمال الزمن الخاص بقسم "${className}"؟`)) {
            try {
                await api.delete(`/timetables/${classId}`);
                success('تم حذف استعمال الزمن بنجاح');
                refresh();
            } catch (err) {
                toastError('خطأ أثناء الحذف');
            }
        }
    };

    const filteredClasses = classes.filter(cls =>
        cls.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-primary" />
                        استعمال الزمن للأقسام
                    </h2>
                    <p className="text-slate-400 text-[11px] leading-none mt-1 font-medium">
                        إدارة تواقيت الدراسة لكل قسم وتحديد الرحلات اليومية لسائقي الحافلات
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        onClick={() => navigate('/print/timetables')}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-lg border border-slate-200 text-slate-600 hover:border-primary hover:text-primary transition-all duration-200"
                    >
                        <Printer className="w-4 h-4" />
                        طباعة استعمال الزمن للسائقين
                    </button>
                    {canCreate && (
                        <Button onClick={() => handleOpenModal()}>
                            <Plus className="w-4 h-4" />
                            أضف استعمال زمن
                        </Button>
                    )}
                </div>
            </div>

            {/* Search */}
            <Card className="p-3">
                <Input
                    placeholder="ابحث عن قسم معين..."
                    icon={Search}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </Card>

            {/* Classes List */}
            {loading ? (
                <div className="text-center py-12 text-slate-400 animate-pulse">جاري تحميل استعمال الزمن...</div>
            ) : filteredClasses.length === 0 ? (
                <Card className="p-12 text-center">
                    <Calendar className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                    <p className="text-sm font-semibold text-slate-500">لا يوجد استعمال زمن مضاف حتى الآن</p>
                    <p className="text-xs text-slate-400 mt-1">قم بالنقر على "أضف استعمال زمن" لضبط مواعيد الأقسام الدراسية</p>
                </Card>
            ) : (
                <div className="grid grid-cols-1 gap-4">
                    {filteredClasses.map((cls) => {
                        const hasTimetable = cls.timetables && cls.timetables.length > 0;
                        return (
                            <Card key={cls.id} className="overflow-hidden hover:border-primary/30 transition-colors">
                                {/* Card Header */}
                                <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50 flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                                            <GraduationCap className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-slate-800">{cls.name}</h3>
                                            <span className="text-[11px] text-slate-400 flex items-center gap-1">
                                                <Users className="w-3 h-3" />
                                                {cls.students_count || 0} تلميذ
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {canCreate && (
                                            <button
                                                onClick={() => handleOpenModal(cls)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:border-primary hover:text-primary transition-all duration-200"
                                            >
                                                <Edit3 className="w-3.5 h-3.5" />
                                                تعديل التواقيت
                                            </button>
                                        )}
                                        {canDelete && hasTimetable && (
                                            <button
                                                onClick={() => handleDeleteTimetable(cls.id, cls.name)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-red-100 text-red-500 hover:bg-red-50 transition-all duration-200"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                                حذف
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Days Grid */}
                                <div className="p-4">
                                    {!hasTimetable ? (
                                        <p className="text-xs text-slate-400 text-center py-3 italic">
                                            لم يتم تحديد أوقات الدراسة لهذا القسم بعد
                                        </p>
                                    ) : (
                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                                            {DAYS.map(day => {
                                                const daySlots = cls.timetables.filter(t => (t.day_of_week || '').trim() === day);
                                                return (
                                                    <div key={day} className="rounded-lg border border-slate-100 bg-slate-50/50 p-2.5">
                                                        <h4 className="font-bold text-[11px] text-slate-600 pb-1.5 border-b border-slate-100 mb-2 flex items-center justify-between">
                                                            <span>{day}</span>
                                                            <span className="text-[10px] font-normal text-slate-400">({daySlots.length})</span>
                                                        </h4>
                                                        {daySlots.length === 0 ? (
                                                            <span className="text-[10px] text-slate-300 block text-center py-1">—</span>
                                                        ) : (
                                                            <div className="space-y-1">
                                                                {daySlots.map(slot => (
                                                                    <div key={slot.id} className="bg-white border border-slate-100 rounded p-1.5 text-center">
                                                                        <div className="text-[11px] font-bold text-primary">
                                                                            {(slot.start_time || '').substring(0, 5)} - {(slot.end_time || '').substring(0, 5)}
                                                                        </div>
                                                                        {slot.notes && (
                                                                            <div className="text-[9px] text-slate-400 mt-0.5 truncate">{slot.notes}</div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Modal */}
            <TimetableModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={refresh}
                initialClass={selectedClass}
                schoolClasses={classes}
            />
        </div>
    );
};
